import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import type { Dashboard } from "../router";
import { signOut } from "../sign-out";
import { app, useFields } from "../store";
import { DesignFrame, useDesignFrame } from "./DesignFrame";
import { Icon } from "./Icon";
import { Logo } from "./Logo";
import { PasswordDialog } from "./PasswordDialog";
import { Empty, Toasts } from "./ui";

/** Administration runs outside the mail frame: no mail sidebar, search, or composer. */
export const DashboardShell = ({
  dashboard,
  children,
}: {
  dashboard: Dashboard;
  children: ComponentChildren;
}) => {
  const { me } = useFields(app, "me");
  const frame = useDesignFrame();
  const allowed = dashboard === "admin" ? me?.isAdmin : (me?.managedDomains ?? []).length > 0;
  const title = dashboard === "admin" ? "Administration" : "Domain administration";
  useEffect(() => {
    document.title = `${title} · ${me?.appName ?? "Cloudmail"}`;
  }, [title, me?.appName]);
  if (!me) return null;
  const header = (
    <header class="dashboard-header" data-part="dashboard-header">
      <a
        data-part="dashboard-brand"
        class="dashboard-brand"
        href={dashboard === "admin" ? "/admin" : "/domain-admin"}
      >
        <Logo appName={me.appName} size={30} />
        <span class="dashboard-heading">
          <strong>{title}</strong>
        </span>
      </a>
      <nav aria-label="Dashboards">
        <span class="dashboard-account">{me.login}</span>
        {me.isAdmin && dashboard !== "admin" && <a href="/admin">Admin</a>}
        {(me.managedDomains ?? []).length > 0 && dashboard !== "domain-admin" && (
          <a href="/domain-admin">Domain admin</a>
        )}
        <a class="dashboard-mail" href="/">
          <Icon name="inbox" size={17} /> Back to mail
        </a>
        <button type="button" class="ghost small" onClick={() => void signOut()}>
          Sign out
        </button>
      </nav>
    </header>
  );
  const main = (
    <main class="dashboard-content" data-part="dashboard-content">
      {allowed ? (
        children
      ) : (
        <Empty
          icon="shield"
          title="You don't have access to this dashboard"
          text="Sign in with an account that has the required administrator access."
        />
      )}
    </main>
  );
  const footer = (
    <>
      <PasswordDialog />
      <Toasts />
    </>
  );
  return (
    <div
      class={`dashboard-shell${frame ? " designed-shell" : ""}`}
      data-part="dashboard-shell"
      data-dashboard={dashboard}
    >
      {frame ? (
        <DesignFrame node={frame} slots={{ header, main, footer, sidebar: null, banner: null }} />
      ) : (
        <>
          {header}
          {main}
          {footer}
        </>
      )}
    </div>
  );
};
